/* ==========================================================================
   Atlas — theme.js
   Single entry module. Defines every custom element and the scroll-reveal
   observer. No build step; ships as-is to the Shopify CDN.
   ========================================================================== */

const Atlas = {
  money(cents) {
    const amount = (cents / 100).toFixed(2);
    return (window.Shopify?.currency?.active === 'USD' ? '$' : '') + amount;
  },
  async getCart() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    return res.json();
  },
  emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  },
  renderCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
    document.documentElement.classList.toggle('cart-has-items', cart.item_count > 0);
  },
  async refreshCart() {
    const cart = await this.getCart();
    this.renderCartCount(cart);
    return cart;
  },
};

/* --- Accessibility: focus trap + cart announcer -------------------------- */
const FocusTrap = {
  SEL: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  trap(container) {
    if (!container) return;
    this.release();
    this.container = container;
    this.prev = document.activeElement;
    this._onKey = (e) => {
      if (e.key !== 'Tab') return;
      const f = Array.from(container.querySelectorAll(this.SEL)).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this._onKey);
    const focusable = container.querySelector(this.SEL);
    requestAnimationFrame(() => (focusable || container).focus({ preventScroll: true }));
  },
  release() {
    if (this._onKey) document.removeEventListener('keydown', this._onKey);
    this._onKey = null;
    const prev = this.prev;
    this.container = null;
    this.prev = null;
    if (prev && prev.focus) {
      try {
        prev.focus({ preventScroll: true });
      } catch (e) {}
    }
  },
};

function initA11y() {
  const live = document.createElement('div');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('role', 'status');
  live.className = 'visually-hidden';
  document.body.appendChild(live);
  document.addEventListener('atlas:cart-add', () => {
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = 'Item added to your cart';
    });
  });
}

/* --- Scroll reveal -------------------------------------------------------- */
function initReveal() {
  const els = document.querySelectorAll('.reveal');
  if (!els.length || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );
  els.forEach((el) => io.observe(el));
}

/* --- <theme-toggle> ------------------------------------------------------- */
class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => this.toggle());
    this.sync();
  }
  current() {
    return document.documentElement.getAttribute('data-theme') || 'light';
  }
  toggle() {
    const next = this.current() === 'dark' ? 'light' : 'dark';
    const root = document.documentElement;

    const apply = () => {
      root.setAttribute('data-theme', next);
      try {
        localStorage.setItem('atlas-theme', next);
      } catch (e) {}
      this.sync();
      Atlas.emit('atlas:theme-change', { theme: next });
    };

    // Enable the global color transition, force the browser to commit it,
    // THEN swap the theme so every surface eases between schemes.
    // Always runs (explicit, user-initiated) — not gated on reduced-motion.
    root.classList.add('theme-anim');
    void root.offsetWidth; // forced reflow registers the transition
    apply();
    window.clearTimeout(this._animTimer);
    this._animTimer = window.setTimeout(() => root.classList.remove('theme-anim'), 1600);
  }
  sync() {
    const isDark = this.current() === 'dark';
    this.button?.setAttribute('aria-pressed', String(isDark));
    this.setAttribute('mode', isDark ? 'dark' : 'light');
  }
}
customElements.define('theme-toggle', ThemeToggle);

/* --- <atlas-header> (pop-out sidebar) ------------------------------------- */
class AtlasHeader extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('[data-menu]');
    this.panel = this.querySelector('.menu__panel');
    this.overlay = this.querySelector('.menu__overlay');
    this.links = Array.from(this.querySelectorAll('.menu__link'));
    this.menuBtn = this.querySelector('[data-menu-toggle]');
    this.searchInput = this.querySelector('input[type="search"], input[name="q"]');
    this.gsap = window.gsap || null;
    if (this.gsap) this.classList.add('gsap');

    this.querySelectorAll('[data-menu-toggle]').forEach((b) =>
      b.addEventListener('click', () => this.toggle())
    );
    this.querySelectorAll('[data-menu-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    this.querySelector('[data-search-toggle]')?.addEventListener('click', () => this.open(true));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    // Fade the centered title once the page is scrolled past the header
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        // Scroll-linked condense: 0 at the top → 1 once past ~160px.
        document.documentElement.style.setProperty('--header-progress', Math.min(y / 160, 1).toFixed(3));
        document.documentElement.classList.toggle('is-scrolled', y > 40);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  toggle() {
    if (this.classList.contains('is-menu-open')) this.close();
    else this.open();
  }
  open(focusSearch) {
    this.classList.add('is-menu-open');
    document.body.classList.add('menu-open');
    this.menu?.setAttribute('aria-hidden', 'false');
    this.menuBtn?.setAttribute('aria-expanded', 'true');

    if (this.gsap) {
      this.gsap.killTweensOf([this.panel, this.overlay, ...this.links]);
      this.gsap
        .timeline()
        .set(this.menu, { visibility: 'visible' })
        .to(this.overlay, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0)
        .fromTo(
          this.panel,
          { autoAlpha: 0, y: -16, scale: 0.96, transformOrigin: 'top right' },
          { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: 'power3.out' },
          0
        )
        .fromTo(
          this.links,
          { autoAlpha: 0, x: 16 },
          { autoAlpha: 1, x: 0, stagger: 0.05, duration: 0.45, ease: 'power3.out' },
          0.16
        );
    }
    if (focusSearch) setTimeout(() => this.searchInput?.focus(), 450);
    FocusTrap.trap(this.panel);
  }
  close() {
    FocusTrap.release();
    this.menu?.setAttribute('aria-hidden', 'true');
    this.menuBtn?.setAttribute('aria-expanded', 'false');

    if (this.gsap) {
      this.gsap.killTweensOf([this.panel, this.overlay]);
      this.gsap
        .timeline({
          onComplete: () => {
            this.classList.remove('is-menu-open');
            document.body.classList.remove('menu-open');
          },
        })
        .to(this.overlay, { autoAlpha: 0, duration: 0.35, ease: 'power2.in' }, 0)
        .to(this.panel, { autoAlpha: 0, y: -12, scale: 0.97, duration: 0.32, ease: 'power2.in' }, 0);
    } else {
      this.classList.remove('is-menu-open');
      document.body.classList.remove('menu-open');
    }
  }
}
customElements.define('atlas-header', AtlasHeader);

/* --- <atlas-drawer> ------------------------------------------------------- */
/* Header variant whose menu is a full-height drawer sliding from the left.
   CSS-driven (no GSAP) so the slide direction stays correct. */
class AtlasDrawer extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('[data-menu]');
    this.menuBtn = this.querySelector('[data-menu-toggle]');
    this.searchInput = this.querySelector('input[type="search"], input[name="q"]');

    this.querySelectorAll('[data-menu-toggle]').forEach((b) =>
      b.addEventListener('click', () => this.toggle())
    );
    this.querySelectorAll('[data-menu-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    this.querySelector('[data-search-toggle]')?.addEventListener('click', () => this.open(true));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        document.documentElement.classList.toggle('is-scrolled', window.scrollY > 40);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  toggle() {
    this.classList.contains('is-menu-open') ? this.close() : this.open();
  }
  open(focusSearch) {
    this.classList.add('is-menu-open');
    document.body.classList.add('menu-open');
    this.menu?.setAttribute('aria-hidden', 'false');
    this.menuBtn?.setAttribute('aria-expanded', 'true');
    if (focusSearch) setTimeout(() => this.searchInput?.focus(), 350);
    FocusTrap.trap(this.querySelector('.menu__panel'));
  }
  close() {
    FocusTrap.release();
    this.classList.remove('is-menu-open');
    document.body.classList.remove('menu-open');
    this.menu?.setAttribute('aria-hidden', 'true');
    this.menuBtn?.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('atlas-drawer', AtlasDrawer);

/* --- <product-showcase> --------------------------------------------------- */
class ProductShowcase extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.total = this.slides.length;
    this.index = 0;
    this.animating = false;
    this.gsap = window.gsap || null;
    this.curEl = this.querySelector('[data-counter-current]');
    this.totalEl = this.querySelector('[data-counter-total]');
    if (this.totalEl) this.totalEl.textContent = String(this.total).padStart(2, '0');

    if (this.gsap) {
      this.classList.add('gsap');
      this.gsap.set(this.slides, { opacity: 0, scale: 0.92, pointerEvents: 'none' });
      this.gsap.set(this.slides[0], { opacity: 1, scale: 1, pointerEvents: 'auto' });
    }

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowRight') this.go(1);
    });

    // Note: no wheel-jacking — the home page scrolls normally so visitors can
    // reach the sections below the hero. Advance via arrows / drag / keyboard.
    this.bindDrag();
    this.updateCounter();
  }
  bindDrag() {
    let startX = 0;
    let dragging = false;
    const down = (x) => {
      startX = x;
      dragging = true;
    };
    const up = (x) => {
      if (!dragging) return;
      dragging = false;
      const dx = x - startX;
      if (Math.abs(dx) > 60) this.go(dx < 0 ? 1 : -1);
    };
    this.addEventListener('pointerdown', (e) => down(e.clientX));
    this.addEventListener('pointerup', (e) => up(e.clientX));
    this.addEventListener('touchstart', (e) => down(e.touches[0].clientX), { passive: true });
    this.addEventListener('touchend', (e) => up(e.changedTouches[0].clientX));
  }
  go(dir) {
    if (this.total < 2 || this.animating) return;
    const from = this.index;
    this.index = (this.index + dir + this.total) % this.total;
    if (this.gsap) {
      this.animate(from, this.index, dir);
    } else {
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    }
    this.updateCounter();
  }
  animate(from, to, dir) {
    this.animating = true;
    const cur = this.slides[from];
    const next = this.slides[to];
    const shift = 9 * dir; // percent of travel
    this.gsap
      .timeline({ onComplete: () => (this.animating = false) })
      .set(next, { zIndex: 2, pointerEvents: 'none', opacity: 0, xPercent: shift * 2, scale: 0.9 })
      .set(cur, { zIndex: 1 })
      .to(cur, { opacity: 0, xPercent: -shift * 2, scale: 0.9, duration: 0.6, ease: 'power3.inOut' }, 0)
      .to(next, { opacity: 1, xPercent: 0, scale: 1, duration: 0.8, ease: 'power3.out' }, 0.14)
      .set(cur, { zIndex: 0, pointerEvents: 'none' })
      .set(next, { zIndex: 1, pointerEvents: 'auto' });
  }
  updateCounter() {
    if (this.curEl) this.curEl.textContent = String(this.index + 1).padStart(2, '0');
  }
}
customElements.define('product-showcase', ProductShowcase);

/* --- <policy-tabs> -------------------------------------------------------- */
class PolicyTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.tabs.forEach((tab) =>
      tab.addEventListener('click', () => this.select(tab.dataset.tab))
    );
  }
  select(id) {
    this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === id));
    this.panels.forEach((p) => p.toggleAttribute('hidden', p.dataset.panel !== id));
  }
}
customElements.define('policy-tabs', PolicyTabs);

/* --- <predictive-search> -------------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"], input[name="q"]');
    this.results = this.querySelector('[data-results]');
    this.timer = null;
    this.input?.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), 250);
    });
  }
  async search() {
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.results.innerHTML = '';
      return;
    }
    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(
      q
    )}&resources[type]=product,collection&resources[limit]=6`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      this.render(data.resources?.results || {});
    } catch (e) {}
  }
  render(results) {
    const products = results.products || [];
    if (!products.length) {
      this.results.innerHTML = `<p class="predictive__empty">${this.dataset.emptyText || 'No results'}</p>`;
      return;
    }
    this.results.innerHTML = products
      .map(
        (p) => `
        <a class="predictive__item" href="${p.url}">
          ${p.image ? `<img src="${p.image}" alt="" width="56" height="56" loading="lazy">` : ''}
          <span class="predictive__title">${p.title}</span>
          <span class="predictive__price">${p.price ? Atlas.money(parseInt(p.price, 10)) : ''}</span>
        </a>`
      )
      .join('');
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* --- <product-form> ------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (e) => this.submit(e));
  }
  async submit(e) {
    e.preventDefault();
    if (!this.button || this.button.hasAttribute('disabled')) return;
    this.button.classList.add('is-loading');
    const data = new FormData(this.form);
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      });
      if (!res.ok) throw new Error('add-failed');
      Atlas.emit('atlas:cart-add');
    } catch (err) {
    } finally {
      this.button.classList.remove('is-loading');
    }
  }
}
customElements.define('product-form', ProductForm);

/* --- <quick-add> ---------------------------------------------------------- */
/* Grid card quick-add. Single-variant products add on the first tap; products
   with options reveal a small tray of variant chips. Reuses the shared
   atlas:cart-add event so the drawer + toast behave exactly like the PDP. */
class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-qa-toggle]');
    this.tray = this.querySelector('[data-qa-tray]');
    this.directVariant = this.dataset.variant || null;
    this.qaStyle = this.dataset.style || 'tray';

    this.toggleBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.qaStyle === 'modal' || this.qaStyle === 'sheet') {
        QuickView.open(this.dataset.handle, this.qaStyle);
      } else if (this.directVariant) {
        this.add(this.directVariant, this.toggleBtn);
      } else {
        this.toggleTray();
      }
    });

    this.querySelectorAll('[data-qa-variant]').forEach((chip) =>
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        this.add(chip.dataset.qaVariant, chip);
      })
    );

    this._onDocClick = (e) => {
      if (!this.contains(e.target)) this.closeTray();
    };
    this._onKey = (e) => {
      if (e.key === 'Escape') this.closeTray();
    };
  }
  toggleTray() {
    this.classList.contains('is-open') ? this.closeTray() : this.openTray();
  }
  openTray() {
    if (!this.tray) return;
    this.classList.add('is-open');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }
  closeTray() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }
  async add(id, btn) {
    if (this._adding) return;
    this._adding = true;
    btn?.classList.add('is-loading');
    try {
      const res = await fetch(this.dataset.url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({ id, quantity: 1 }),
      });
      if (!res.ok) throw new Error('add-failed');
      btn?.classList.remove('is-loading');
      btn?.classList.add('is-done');
      Atlas.emit('atlas:cart-add');
      this.closeTray();
      setTimeout(() => btn?.classList.remove('is-done'), 1400);
    } catch (err) {
      btn?.classList.remove('is-loading');
    } finally {
      this._adding = false;
    }
  }
}
customElements.define('quick-add', QuickAdd);

/* --- Quick view (modal / bottom-sheet quick-add) -------------------------- */
/* A single shared overlay built lazily on first use, so it stays global
   without touching the layout. Fed by /products/{handle}.js. */
const QuickView = {
  STR: { add: 'Add to cart', soldout: 'Sold out', unavailable: 'Unavailable', details: 'View full details' },
  escHtml(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  },
  build() {
    if (this.el) return;
    const el = document.createElement('div');
    el.className = 'qv';
    el.innerHTML = `
      <div class="qv__backdrop" data-qv-close></div>
      <div class="qv__panel" role="dialog" aria-modal="true" aria-label="Quick view">
        <button class="qv__close" type="button" data-qv-close aria-label="Close">&times;</button>
        <div class="qv__body" data-qv-body></div>
      </div>`;
    document.body.appendChild(el);
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-qv-close]')) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    this.el = el;
    this.bodyEl = el.querySelector('[data-qv-body]');
  },
  async open(handle, style) {
    if (!handle) return;
    this.build();
    this.el.classList.remove('qv--modal', 'qv--sheet');
    this.el.classList.add(style === 'sheet' ? 'qv--sheet' : 'qv--modal');
    this.bodyEl.innerHTML = '<div class="qv__spinner" aria-hidden="true"></div>';
    this.el.classList.add('is-open');
    document.body.classList.add('qv-open');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('not-found');
      this.render(await res.json());
      FocusTrap.trap(this.el.querySelector('.qv__panel'));
    } catch (err) {
      this.close();
    }
  },
  render(product) {
    this.product = product;
    const firstAvail = product.variants.find((v) => v.available) || product.variants[0];
    this.selected = firstAvail.options.slice();
    this.images = product.images && product.images.length ? product.images : [product.featured_image].filter(Boolean);
    const mainImg =
      (firstAvail.featured_image && firstAvail.featured_image.src) || this.images[0] || '';

    const thumbs =
      this.images.length > 1
        ? `<div class="qv__thumbs">${this.images
            .map(
              (src, i) =>
                `<button class="qv__thumb${src === mainImg ? ' is-active' : ''}" type="button" data-qv-thumb="${i}"><img src="${src}" alt="" loading="lazy"></button>`
            )
            .join('')}</div>`
        : '';

    const options = product.options
      .map(
        (opt, oi) => `
        <div class="qv__option">
          <span class="quick-add__label">${this.escHtml(opt.name)}</span>
          <div class="quick-add__chips">
            ${opt.values
              .map(
                (val) =>
                  `<button class="quick-add__chip" type="button" data-qv-oi="${oi}" data-qv-val="${encodeURIComponent(
                    val
                  )}">${this.escHtml(val)}</button>`
              )
              .join('')}
          </div>
        </div>`
      )
      .join('');

    const desc = product.description ? `<div class="qv__desc rte">${product.description}</div>` : '';

    this.bodyEl.innerHTML = `
      <div class="qv__media">
        <img data-qv-img src="${mainImg}" alt="${this.escHtml(product.title)}" loading="lazy">
        ${thumbs}
      </div>
      <div class="qv__info">
        <h3 class="qv__title h-mono">${this.escHtml(product.title)}</h3>
        <div class="qv__price" data-qv-price></div>
        <div class="qv__options">${options}</div>
        ${desc}
        <div class="qv__actions">
          <button class="btn qv__add" type="button" data-qv-add>${this.STR.add}</button>
          <a class="qv__full" href="${product.url}">${this.STR.details}</a>
        </div>
      </div>`;

    this.bodyEl.querySelectorAll('[data-qv-val]').forEach((b) =>
      b.addEventListener('click', () => {
        this.selected[Number(b.dataset.qvOi)] = decodeURIComponent(b.dataset.qvVal);
        this.sync();
      })
    );
    this.bodyEl.querySelectorAll('[data-qv-thumb]').forEach((b) =>
      b.addEventListener('click', () => this.setImage(this.images[Number(b.dataset.qvThumb)]))
    );
    this.bodyEl.querySelector('[data-qv-add]').addEventListener('click', () => this.addCurrent());
    this.sync();
  },
  setImage(src) {
    if (!src) return;
    const img = this.bodyEl.querySelector('[data-qv-img]');
    if (img) img.src = src;
    this.bodyEl.querySelectorAll('[data-qv-thumb]').forEach((t) =>
      t.classList.toggle('is-active', this.images[Number(t.dataset.qvThumb)] === src)
    );
  },
  currentVariant() {
    return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
  },
  sync() {
    this.bodyEl.querySelectorAll('[data-qv-val]').forEach((b) => {
      const selected = this.selected[Number(b.dataset.qvOi)] === decodeURIComponent(b.dataset.qvVal);
      b.classList.toggle('is-selected', selected);
    });
    const v = this.currentVariant();
    const priceEl = this.bodyEl.querySelector('[data-qv-price]');
    const addBtn = this.bodyEl.querySelector('[data-qv-add]');
    if (v) {
      priceEl.textContent = Atlas.money(v.price);
      if (v.featured_image && v.featured_image.src) this.setImage(v.featured_image.src);
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? this.STR.add : this.STR.soldout;
    } else {
      addBtn.disabled = true;
      addBtn.textContent = this.STR.unavailable;
    }
  },
  async addCurrent() {
    const v = this.currentVariant();
    if (!v || !v.available) return;
    const addBtn = this.bodyEl.querySelector('[data-qv-add]');
    addBtn.classList.add('is-loading');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({ id: v.id, quantity: 1 }),
      });
      if (!res.ok) throw new Error('add-failed');
      Atlas.emit('atlas:cart-add');
      this.close();
    } catch (err) {
    } finally {
      addBtn.classList.remove('is-loading');
    }
  },
  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    document.body.classList.remove('qv-open');
    FocusTrap.release();
  },
};

/* --- <cart-drawer> -------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.itemsEl = this.querySelector('[data-cart-items]');
    this.countEls = document.querySelectorAll('[data-cart-count]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.emptyEl = this.querySelector('[data-cart-empty]');
    this.contentEl = this.querySelector('[data-cart-content]');

    this.querySelectorAll('[data-cart-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        this.open();
      }
    });
    document.addEventListener('atlas:cart-add', () => {
      this.refresh().then(() => {
        // The dropdown style stays closed on add (drawer still auto-opens)
        if (!this.classList.contains('cart-drawer--dropdown')) this.open();
      });
    });

    this.itemsEl?.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-line-remove]');
      if (remove) {
        e.preventDefault();
        this.change(remove.dataset.lineRemove, 0);
      }
    });

    // Drawer extras
    this.shipEl = this.querySelector('[data-cart-ship]');
    this.shipFill = this.querySelector('[data-cart-ship-fill]');
    this.shipMsg = this.querySelector('[data-cart-ship-msg]');
    this.noteEl = this.querySelector('[data-cart-note]');
    this.upsellEl = this.querySelector('[data-cart-upsell]');
    this.freeShip = parseInt(this.dataset.freeShip, 10) || 0;
    this.giftId = this.dataset.giftId || '';
    this.giftThreshold = parseInt(this.dataset.giftThreshold, 10) || 0;
    this.giftLabel = this.dataset.giftLabel || 'Gift';

    this.noteEl?.addEventListener('change', () => this.saveNote());
    this.querySelector('[data-cart-discount]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const code = e.target.querySelector('input').value.trim();
      if (code) {
        const root = window.Shopify?.routes?.root || '/';
        window.location.href = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(root + 'cart')}`;
      }
    });
    this.querySelector('[data-cart-upsell-add]')?.addEventListener('click', (e) => {
      this.addVariant(e.currentTarget.dataset.cartUpsellAdd, e.currentTarget);
    });

    this.refresh();
  }
  async addVariant(id, btn) {
    btn?.classList.add('is-loading');
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({ id, quantity: 1 }),
      });
      Atlas.emit('atlas:cart-add');
    } catch (e) {
    } finally {
      btn?.classList.remove('is-loading');
    }
  }
  async saveNote() {
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: this.noteEl.value }),
      });
    } catch (e) {}
  }
  open() {
    this.setAttribute('open', '');
    document.body.classList.add('cart-open');
    FocusTrap.trap(this.querySelector('.cart-drawer__panel'));
  }
  close() {
    this.removeAttribute('open');
    document.body.classList.remove('cart-open');
    FocusTrap.release();
  }
  async change(line, quantity) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity }),
    });
    const cart = await res.json();
    this.render(cart);
  }
  async changeById(id, quantity) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(id), quantity }),
    });
    return res.json();
  }
  // Auto-add / remove a free gift once the cart crosses the threshold.
  reconcileGift(cart) {
    if (!this.giftId || this.giftThreshold <= 0 || this._giftBusy) return;
    const giftLine = cart.items.find((it) => String(it.variant_id) === this.giftId);
    const exGift = cart.total_price - (giftLine ? giftLine.final_line_price : 0);
    if (exGift >= this.giftThreshold && !giftLine && cart.item_count > 0) {
      this._giftBusy = true;
      this.addVariant(this.giftId).finally(() => { this._giftBusy = false; });
    } else if (exGift < this.giftThreshold && giftLine) {
      this._giftBusy = true;
      this.changeById(this.giftId, 0)
        .then((c) => this.render(c))
        .finally(() => { this._giftBusy = false; });
    }
  }
  async refresh() {
    const cart = await Atlas.getCart();
    this.render(cart);
  }
  render(cart) {
    this.countEls.forEach((el) => (el.textContent = cart.item_count));
    document.documentElement.classList.toggle('cart-has-items', cart.item_count > 0);
    if (this.subtotalEl) this.subtotalEl.textContent = Atlas.money(cart.total_price);
    const empty = cart.item_count === 0;
    this.emptyEl?.toggleAttribute('hidden', !empty);
    this.contentEl?.toggleAttribute('hidden', empty);

    // Free-shipping progress
    if (this.shipEl && this.freeShip > 0) {
      const remaining = this.freeShip - cart.total_price;
      const pct = Math.min(100, (cart.total_price / this.freeShip) * 100);
      if (this.shipFill) this.shipFill.style.width = pct + '%';
      if (this.shipMsg) {
        this.shipMsg.textContent =
          remaining <= 0
            ? this.shipEl.dataset.msgReached
            : (this.shipEl.dataset.msgProgress || '').replace('__AMT__', Atlas.money(remaining));
      }
      this.shipEl.classList.toggle('is-reached', remaining <= 0);
    }
    // Order note (don't clobber while typing)
    if (this.noteEl && document.activeElement !== this.noteEl) this.noteEl.value = cart.note || '';
    // Hide upsell once it's in the cart
    if (this.upsellEl) {
      const inCart = cart.items.some((it) => it.handle === this.upsellEl.dataset.upsellHandle);
      this.upsellEl.toggleAttribute('hidden', inCart || empty);
    }

    if (this.itemsEl) {
      this.itemsEl.innerHTML = cart.items
        .map((item, i) => {
          const isGift = this.giftId && String(item.variant_id) === this.giftId;
          return `
        <li class="cart-line${isGift ? ' cart-line--gift' : ''}">
          <a class="cart-line__media" href="${item.url}">
            ${item.image ? `<img src="${item.image}" alt="" width="72" height="72" loading="lazy">` : ''}
          </a>
          <div class="cart-line__info">
            <a class="cart-line__title" href="${item.url}">${item.product_title}</a>
            ${isGift ? `<span class="cart-line__gift">${this.giftLabel}</span>` : item.variant_title ? `<span class="cart-line__variant">${item.variant_title}</span>` : ''}
            <span class="cart-line__price">${item.quantity} &times; ${Atlas.money(item.final_price)}</span>
          </div>
          ${isGift ? '' : `<button class="cart-line__remove" type="button" data-line-remove="${i + 1}" aria-label="Remove">&times;</button>`}
        </li>`;
        })
        .join('');
    }

    this.reconcileGift(cart);
  }
}
customElements.define('cart-drawer', CartDrawer);

/* --- <media-gallery> ------------------------------------------------------ */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.stage = document.querySelector('[data-stage]');
    this.medias = Array.from(this.stage?.querySelectorAll('[data-media]') || []);
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.gsap = window.gsap || null;
    this.animating = false;
    this.index = Math.max(0, this.medias.findIndex((m) => m.classList.contains('is-active')));

    if (this.gsap && this.stage) {
      this.stage.classList.add('gsap');
      this.gsap.set(this.medias, { opacity: 0 });
      this.gsap.set(this.medias[this.index], { opacity: 1, scale: 1 });
    }

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.select(thumb.dataset.thumb))
    );

    // Wheel/drag image browsing only suits the scroll-locked floating layout —
    // not when there are sections below the product that need page scrolling.
    const floating = this.closest('.product')?.classList.contains('product--floating');
    const hasSectionsBelow = document.querySelector('related-products, recently-viewed, .product__app-blocks');
    if (this.medias.length > 1 && floating && !hasSectionsBelow) this.bindScroll();
  }
  indexOf(id) {
    return this.medias.findIndex((m) => m.dataset.media === String(id));
  }
  select(id) {
    const to = this.indexOf(id);
    if (to < 0) return;
    this.go(to);
  }
  go(to) {
    if (to === this.index || this.animating || to < 0 || to >= this.medias.length) return;
    const dir = to > this.index ? 1 : -1;
    const cur = this.medias[this.index];
    const next = this.medias[to];
    this.index = to;

    this.thumbs.forEach((t) =>
      t.classList.toggle('is-active', t.dataset.thumb === next.dataset.media)
    );

    if (this.gsap) {
      this.animating = true;
      this.gsap
        .timeline({ onComplete: () => (this.animating = false) })
        .set(next, { zIndex: 2, opacity: 0, xPercent: 9 * dir, scale: 0.96 })
        .set(cur, { zIndex: 1 })
        .to(cur, { opacity: 0, xPercent: -7 * dir, scale: 0.97, duration: 0.4, ease: 'power2.inOut' }, 0)
        .to(next, { opacity: 1, xPercent: 0, scale: 1, duration: 0.55, ease: 'power3.out' }, 0.1)
        .set(cur, { zIndex: 0 });
    }
    this.medias.forEach((m, i) => m.classList.toggle('is-active', i === to));

    // Pause videos we're leaving; play the one we're showing (muted)
    const curVideo = cur.querySelector('video');
    if (curVideo) curVideo.pause();
    const nextVideo = next.querySelector('video');
    if (nextVideo) nextVideo.play().catch(() => {});
  }
  step(dir) {
    const to = (this.index + dir + this.medias.length) % this.medias.length;
    this.go(to);
  }
  bindScroll() {
    let lock = false;
    this.stage.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaY) < 12 || lock || this.animating) return;
        lock = true;
        this.step(e.deltaY > 0 ? 1 : -1);
        setTimeout(() => (lock = false), 600);
      },
      { passive: true }
    );
    let sx = 0;
    let drag = false;
    this.stage.addEventListener('pointerdown', (e) => {
      sx = e.clientX;
      drag = true;
    });
    this.stage.addEventListener('pointerup', (e) => {
      if (!drag) return;
      drag = false;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
    });
  }
}
customElements.define('media-gallery', MediaGallery);

/* --- <product-lightbox>: full-screen zoomable image viewer ----------------- */
class ProductLightbox extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-lightbox-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
    this.imgs = Array.from(this.querySelectorAll('.lightbox__img'));
    this.currentEl = this.querySelector('[data-lightbox-current]');
    this.index = 0;
    this._sx = 0;
    this._moved = false;

    document.querySelectorAll('[data-zoom]').forEach((el) => {
      el.classList.add('is-zoomable');
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.openByMedia(el.dataset.zoom);
      });
    });
    this.querySelector('[data-lightbox-close]')?.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index - 1); });
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index + 1); });

    // Tap the image to zoom; tap anywhere off the image to close; drag to swipe.
    this.addEventListener('pointerdown', (e) => { this._sx = e.clientX; this._moved = false; });
    this.addEventListener('pointermove', (e) => { if (Math.abs(e.clientX - this._sx) > 10) this._moved = true; });
    this.addEventListener('pointerup', (e) => {
      const dx = e.clientX - this._sx;
      if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
    });
    this.addEventListener('click', (e) => {
      if (e.target.closest('button')) return;
      if (this._moved) { this._moved = false; return; }
      const img = e.target.closest('.lightbox__img');
      if (img) this.toggleZoom(img, e);
      else this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') this.close();
      else if (e.key === 'ArrowLeft') this.go(this.index - 1);
      else if (e.key === 'ArrowRight') this.go(this.index + 1);
    });
  }
  openByMedia(mediaId) {
    const i = this.slides.findIndex((s) => s.dataset.lightboxMedia === String(mediaId));
    this.open(i < 0 ? 0 : i);
  }
  open(i) {
    this.index = i;
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.setAttribute('open', ''));
    document.body.classList.add('modal-open');
    this.update();
    FocusTrap.trap(this);
  }
  close() {
    this.removeAttribute('open');
    document.body.classList.remove('modal-open');
    this.resetZoom();
    FocusTrap.release();
    setTimeout(() => this.setAttribute('hidden', ''), 300);
  }
  go(n) {
    const len = this.slides.length;
    if (len < 2) return;
    this.index = (n + len) % len;
    this.resetZoom();
    this.update();
  }
  update() {
    if (this.stage) this.stage.style.transform = `translateX(${-this.index * 100}%)`;
    if (this.currentEl) this.currentEl.textContent = this.index + 1;
  }
  toggleZoom(img, e) {
    if (img.classList.contains('is-zoomed')) {
      this.resetZoom();
    } else {
      const r = img.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
      img.classList.add('is-zoomed');
    }
  }
  resetZoom() {
    this.imgs.forEach((s) => {
      s.classList.remove('is-zoomed');
      s.style.transformOrigin = '';
    });
  }
}
customElements.define('product-lightbox', ProductLightbox);

/* --- <related-products> --------------------------------------------------- */
/* Lazily fetches Shopify product recommendations (rendered with this section)
   and swaps the placeholder for real product cards. */
class RelatedProducts extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this._loaded) return;
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    io.observe(this);
  }
  async load() {
    this._loaded = true;
    try {
      const res = await fetch(this.dataset.url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('related-products');
      const grid = fresh && fresh.querySelector('.related__grid');
      if (grid && !grid.classList.contains('related__grid--loading') && grid.children.length) {
        this.innerHTML = fresh.innerHTML;
        this.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
        document.dispatchEvent(new CustomEvent('atlas:wishlist-change'));
      } else {
        this.remove(); // no recommendations available — hide entirely
      }
    } catch (e) {}
  }
}
customElements.define('related-products', RelatedProducts);

/* --- <recently-viewed> ---------------------------------------------------- */
/* Records the current product in localStorage and renders the previously
   viewed ones. Lives in the product template, so it records on every PDP. */
class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle || '';
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.grid = this.querySelector('[data-rv-grid]');
    this.record();
    this.render();
  }
  list() {
    try {
      return JSON.parse(localStorage.getItem('atlas-recent')) || [];
    } catch (e) {
      return [];
    }
  }
  record() {
    if (!this.handle) return;
    let list = this.list().filter((h) => h !== this.handle);
    list.unshift(this.handle);
    list = list.slice(0, 20);
    try {
      localStorage.setItem('atlas-recent', JSON.stringify(list));
    } catch (e) {}
  }
  escHtml(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }
  async render() {
    const handles = this.list().filter((h) => h !== this.handle).slice(0, this.limit);
    if (!handles.length) {
      this.remove();
      return;
    }
    const root = window.Shopify?.routes?.root || '/';
    const products = await Promise.all(
      handles.map((h) =>
        fetch(`${root}products/${h}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    const valid = products.filter(Boolean);
    if (!valid.length || !this.grid) {
      this.remove();
      return;
    }
    this.grid.innerHTML = valid.map((p) => this.card(p)).join('');
    this.classList.add('rv-ready');
  }
  card(p) {
    const img = p.featured_image || (p.images && p.images[0]) || '';
    return `
      <a class="rv-card" href="${p.url}">
        <span class="rv-card__media">${img ? `<img src="${img}" alt="${this.escHtml(p.title)}" loading="lazy">` : ''}</span>
        <span class="rv-card__info">
          <span class="rv-card__title h-mono">${this.escHtml(p.title)}</span>
          <span class="rv-card__price">${Atlas.money(p.price)}</span>
        </span>
      </a>`;
  }
}
customElements.define('recently-viewed', RecentlyViewed);

/* --- <sticky-atc> --------------------------------------------------------- */
/* A bottom bar that appears once the main Add-to-cart scrolls out of view and
   proxies clicks to the real button, mirroring its price + availability. */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.mainBtn = document.querySelector('[data-atc]');
    this.mainPrice = document.querySelector('[data-atc-price]');
    this.stickyBtn = this.querySelector('[data-sticky-atc]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.stickyText = this.querySelector('[data-sticky-text]');
    if (!this.mainBtn || !this.stickyBtn) return;

    this.stickyBtn.addEventListener('click', () => this.mainBtn.click());

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => this.classList.toggle('is-visible', !entries[0].isIntersecting),
        { rootMargin: '0px 0px -10% 0px' }
      );
      io.observe(this.mainBtn);
    }

    const sync = () => {
      const disabled = this.mainBtn.hasAttribute('disabled');
      this.stickyBtn.toggleAttribute('disabled', disabled);
      if (this.mainPrice && this.stickyPrice) this.stickyPrice.textContent = this.mainPrice.textContent;
      const mainText = this.mainBtn.querySelector('[data-atc-text]');
      if (mainText && this.stickyText) this.stickyText.textContent = mainText.textContent;
    };
    new MutationObserver(sync).observe(this.mainBtn, { attributes: true, childList: true, subtree: true });
    sync();
  }
}
customElements.define('sticky-atc', StickyAtc);

/* --- <shop-look>: image with product hotspots ----------------------------- */
class ShopLook extends HTMLElement {
  connectedCallback() {
    this.spots = Array.from(this.querySelectorAll('[data-hotspot]'));
    this.querySelectorAll('[data-look-pin]').forEach((pin) => {
      pin.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const spot = pin.closest('[data-hotspot]');
        const open = spot.classList.contains('is-open');
        this.closeAll();
        if (!open) spot.classList.add('is-open');
      });
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });
  }
  closeAll() {
    this.spots.forEach((s) => s.classList.remove('is-open'));
  }
}
customElements.define('shop-look', ShopLook);

/* --- <faq-accordion>: CSS grid-rows animated accordion -------------------- */
class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.single = this.dataset.single === 'true';
    this.querySelectorAll('.faq__q').forEach((btn) =>
      btn.addEventListener('click', () => this.toggle(btn))
    );
  }
  reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  toggle(btn) {
    const item = btn.closest('.faq__item');
    if (!item) return;
    const willOpen = !item.classList.contains('is-open');
    if (this.single && willOpen) {
      this.querySelectorAll('.faq__item.is-open').forEach((other) => {
        if (other !== item) this.collapse(other);
      });
    }
    if (willOpen) this.expand(item);
    else this.collapse(item);
  }
  expand(item) {
    const panel = item.querySelector('.faq__panel');
    item.querySelector('.faq__q')?.setAttribute('aria-expanded', 'true');
    if (!panel) { item.classList.add('is-open'); return; }
    if (this.reduced()) { item.classList.add('is-open'); panel.style.height = 'auto'; return; }
    const target = panel.scrollHeight;   // measure full content while collapsed
    panel.style.height = '0px';          // pin the start
    panel.offsetHeight;                  // force a reflow so 0px is the transition start
    item.classList.add('is-open');       // icon rotates (inline height overrides the class)
    panel.style.height = target + 'px';  // animate 0 -> target
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      if (item.classList.contains('is-open')) panel.style.height = 'auto';
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
  }
  collapse(item) {
    const panel = item.querySelector('.faq__panel');
    item.querySelector('.faq__q')?.setAttribute('aria-expanded', 'false');
    if (!panel) { item.classList.remove('is-open'); return; }
    if (this.reduced()) { item.classList.remove('is-open'); panel.style.height = '0px'; return; }
    panel.style.height = panel.scrollHeight + 'px';  // from auto to an explicit start
    panel.offsetHeight;                              // force a reflow to commit it
    item.classList.remove('is-open');
    panel.style.height = '0px';                      // animate -> 0
  }
}
customElements.define('faq-accordion', FaqAccordion);

/* --- <count-up>: animate numbers when scrolled into view ------------------ */
class CountUp extends HTMLElement {
  connectedCallback() {
    this.targets = Array.from(this.querySelectorAll('[data-count-to]'));
    if (!this.targets.length) return;
    if (!('IntersectionObserver' in window)) {
      this.targets.forEach((el) => (el.textContent = this.format(el, parseFloat(el.dataset.countTo))));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.targets.forEach((el) => this.run(el));
      },
      { threshold: 0.3 }
    );
    io.observe(this);
  }
  format(el, n) {
    const decimals = parseInt(el.dataset.decimals, 10) || 0;
    return n.toFixed(decimals);
  }
  run(el) {
    const to = parseFloat(el.dataset.countTo) || 0;
    const dur = 1600;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = this.format(el, to * eased);
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = this.format(el, to);
    };
    requestAnimationFrame(tick);
  }
}
customElements.define('count-up', CountUp);

/* --- <size-guide> --------------------------------------------------------- */
class SizeGuide extends HTMLElement {
  connectedCallback() {
    document.querySelectorAll('[data-size-guide-open]').forEach((b) =>
      b.addEventListener('click', () => this.open())
    );
    this.querySelectorAll('[data-size-guide-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }
  open() {
    this.classList.add('is-open');
    document.body.classList.add('modal-open');
  }
  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('modal-open');
  }
}
customElements.define('size-guide', SizeGuide);

/* --- <variant-selects> ---------------------------------------------------- */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.idInput = this.querySelector('[data-variant-id]');
    this.addEventListener('change', () => this.update());
  }
  selectedOptions() {
    const checked = Array.from(this.querySelectorAll('input[type="radio"]:checked'));
    return checked
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((i) => i.value);
  }
  update() {
    const opts = this.selectedOptions();
    if (!opts.length) return;
    const match = this.variants.find((v) =>
      v.options.every((o, i) => o === opts[i])
    );
    if (!match) return;

    if (this.idInput) this.idInput.value = match.id;

    const priceEl = document.querySelector('[data-atc-price]');
    if (priceEl) priceEl.textContent = Atlas.money(match.price);

    const btn = document.querySelector('[data-atc]');
    const txt = document.querySelector('[data-atc-text]');
    if (btn) {
      btn.toggleAttribute('disabled', !match.available);
      if (txt) txt.textContent = match.available ? btn.dataset.addText || 'Add to cart' : btn.dataset.soldText || 'Sold out';
    }

    if (match.featured_media) {
      const id = String(match.featured_media.id);
      const gallery = document.querySelector('media-gallery');
      if (gallery && typeof gallery.select === 'function') {
        gallery.select(id);
      } else {
        document
          .querySelectorAll('[data-media]')
          .forEach((m) => m.classList.toggle('is-active', m.dataset.media === id));
      }
    }
    Atlas.emit('atlas:variant-change', { variant: match });
  }
}
customElements.define('variant-selects', VariantSelects);

/* --- Cart toast ----------------------------------------------------------- */
const CartToast = {
  show(count) {
    const el = document.querySelector('cart-toast');
    if (!el) return;
    const num = el.querySelector('[data-toast-count]');
    if (num) num.textContent = count;
    el.classList.add('is-visible');
    window.clearTimeout(this._timer);
    this._timer = window.setTimeout(() => el.classList.remove('is-visible'), 3500);
  },
};

/* --- Global cart manager (works in both drawer and page modes) ------------ */
function initCart() {
  Atlas.refreshCart();
  document.addEventListener('atlas:cart-add', async () => {
    const cart = await Atlas.refreshCart();
    // Show the toast unless a drawer auto-opens (drawer mode). Dropdown and
    // page modes don't auto-open, so the toast provides the confirmation.
    const drawer = document.querySelector('cart-drawer');
    const autoOpens = drawer && !drawer.classList.contains('cart-drawer--dropdown');
    if (!autoOpens) CartToast.show(cart.item_count);
  });
}

/* --- <announcement-bar> --------------------------------------------------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    // In the theme editor the bar always returns on refresh so merchants can
    // see it; only real visitors have their dismissal remembered.
    this.editor = !!(window.Shopify && window.Shopify.designMode);
    if (this.key && !this.editor) {
      try {
        if (localStorage.getItem(this.key) === '1') {
          this.dismiss(true);
          return;
        }
      } catch (e) {}
    }
    this.querySelector('[data-announcement-close]')?.addEventListener('click', () => this.dismiss());

    this.mode = this.dataset.style || 'marquee';
    if (this.mode === 'rotate') {
      this.initRotate();
      return;
    }

    // marquee + split share the scrolling track; countdown has none.
    this.viewport = this.querySelector('.announcement__viewport');
    this.track = this.querySelector('[data-track]');
    this.seq = this.querySelector('[data-seq]');
    this.duration = parseFloat(this.dataset.duration) || 14;
    if (this.seq && this.track && this.viewport) {
      this.build();
      if (document.fonts) document.fonts.ready.then(() => this.build());
      let t;
      window.addEventListener('resize', () => {
        window.clearTimeout(t);
        t = window.setTimeout(() => this.build(), 200);
      });
    }
  }
  initRotate() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length <= 1) return;
    this.idx = 0;
    this.gap = (parseFloat(this.dataset.interval) || 4) * 1000;
    this.querySelector('[data-ann-prev]')?.addEventListener('click', () => this.go(this.idx - 1, true));
    this.querySelector('[data-ann-next]')?.addEventListener('click', () => this.go(this.idx + 1, true));
    this.addEventListener('mouseenter', () => this.stopRotate());
    this.addEventListener('mouseleave', () => this.startRotate());
    this.startRotate();
  }
  go(n, manual) {
    const len = this.slides.length;
    this.slides[this.idx].classList.remove('is-active');
    this.idx = (n + len) % len;
    this.slides[this.idx].classList.add('is-active');
    if (manual) this.startRotate();
  }
  startRotate() {
    this.stopRotate();
    this.rotTimer = window.setInterval(() => this.go(this.idx + 1), this.gap);
  }
  stopRotate() {
    window.clearInterval(this.rotTimer);
  }
  build() {
    const seqW = this.seq.scrollWidth;
    const viewport = this.viewport.clientWidth;
    if (!seqW || !viewport) return;
    if (seqW === this._seqW && viewport === this._vw) return;
    this._seqW = seqW;
    this._vw = viewport;

    Array.from(this.track.children).forEach((c) => {
      if (c !== this.seq) c.remove();
    });

    const copies = Math.ceil(viewport / seqW) + 2;
    for (let i = 1; i < copies; i += 1) {
      const clone = this.seq.cloneNode(true);
      clone.removeAttribute('data-seq');
      clone.setAttribute('aria-hidden', 'true');
      clone
        .querySelectorAll('[data-shopify-editor-block]')
        .forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      this.track.appendChild(clone);
    }

    this.track.style.setProperty('--seq-w', `${seqW}px`);
    this.track.style.setProperty('animation-name', 'announcement-scroll', 'important');
    this.track.style.setProperty('animation-timing-function', 'linear', 'important');
    this.track.style.setProperty('animation-iteration-count', 'infinite', 'important');
    this.track.style.setProperty('animation-duration', `${this.duration}s`, 'important');
  }
  dismiss(silent) {
    this.style.display = 'none';
    document.documentElement.style.setProperty('--announcement-height', '0px');
    if (!silent && this.key && !this.editor) {
      try {
        localStorage.setItem(this.key, '1');
      } catch (e) {}
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* --- <countdown-timer> ---------------------------------------------------- */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.units = this.querySelector('[data-units]');
    this.expiredMsg = this.querySelector('[data-expired-msg]');
    this.d = this.querySelector('[data-days]');
    this.h = this.querySelector('[data-hours]');
    this.m = this.querySelector('[data-mins]');
    this.s = this.querySelector('[data-secs]');

    let offset = this.dataset.offset || '';
    if (/^[+-]\d{4}$/.test(offset)) offset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
    const iso = (this.dataset.end || '').trim().replace(' ', 'T') + offset;
    this.end = new Date(iso).getTime();
    if (isNaN(this.end)) return;

    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }
  pad(n) {
    return String(Math.max(0, n)).padStart(2, '0');
  }
  tick() {
    const diff = this.end - Date.now();
    if (diff <= 0) {
      window.clearInterval(this.timer);
      if (this.units) this.units.hidden = true;
      const msg = this.dataset.expired;
      if (msg && this.expiredMsg) {
        this.expiredMsg.textContent = msg;
        this.expiredMsg.hidden = false;
      }
      return;
    }
    if (this.d) this.d.textContent = this.pad(Math.floor(diff / 86400000));
    if (this.h) this.h.textContent = this.pad(Math.floor(diff / 3600000) % 24);
    if (this.m) this.m.textContent = this.pad(Math.floor(diff / 60000) % 60);
    if (this.s) this.s.textContent = this.pad(Math.floor(diff / 1000) % 60);
  }
}
customElements.define('countdown-timer', CountdownTimer);

/* --- Section fade: smooth fade + lift in/out as sections scroll ----------- */
function initSectionFade() {
  const t = document.body.classList;
  // Long single-grid pages reveal per-card (via .reveal) instead of fading the
  // whole section — a tall section can never satisfy a % threshold until you
  // scroll deep into it, which left these pages blank on load.
  if (
    t.contains('template-product') ||
    t.contains('template-collection') ||
    t.contains('template-search') ||
    t.contains('template-list-collections')
  ) {
    return;
  }
  const sections = Array.from(document.querySelectorAll('#main-content > .shopify-section'));
  if (!sections.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => en.target.classList.toggle('section-fx--in', en.isIntersecting));
    },
    // threshold 0 so even a section taller than the viewport reveals as soon
    // as it enters, rather than waiting for a fixed % of its height.
    { threshold: 0, rootMargin: '0px 0px -8% 0px' }
  );
  sections.forEach((s) => {
    s.classList.add('section-fx');
    io.observe(s);
  });
}

/* --- Hero scroll cue: glide to the content below the hero ----------------- */
function initHeroScroll() {
  document.addEventListener('click', (e) => {
    const cue = e.target.closest('[data-hero-scroll]');
    if (!cue) return;
    const section = cue.closest('.shopify-section') || cue.closest('.hero');
    const next = section?.nextElementSibling;
    if (next && next.scrollIntoView) {
      next.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: window.innerHeight * 0.9, behavior: 'smooth' });
    }
  });
}

/* --- Boot ----------------------------------------------------------------- */
/* --- <newsletter-popup>: timed email-capture modal ------------------------ */
class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.key = 'atlas-np-' + (this.dataset.sectionId || '');
    this.days = parseInt(this.dataset.days, 10) || 7;
    this.querySelectorAll('[data-np-close]').forEach((b) => b.addEventListener('click', () => this.close(true)));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hasAttribute('hidden')) this.close(true);
    });
    // Always preview in the theme editor; otherwise respect the dismissal window.
    if (window.Shopify && window.Shopify.designMode) {
      this.open();
      return;
    }
    if (this.dismissed()) return;
    const delay = (parseFloat(this.dataset.delay) || 4) * 1000;
    this._t = window.setTimeout(() => this.open(), delay);
  }
  dismissed() {
    try {
      const t = parseInt(localStorage.getItem(this.key), 10);
      return t ? Date.now() - t < this.days * 86400000 : false;
    } catch (e) {
      return false;
    }
  }
  open() {
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.body.classList.add('np-open');
    FocusTrap.trap(this.querySelector('.np__panel'));
  }
  close(remember) {
    this.classList.remove('is-open');
    document.body.classList.remove('np-open');
    FocusTrap.release();
    if (remember) {
      try {
        localStorage.setItem(this.key, String(Date.now()));
      } catch (e) {}
    }
    setTimeout(() => this.setAttribute('hidden', ''), 300);
  }
}
customElements.define('newsletter-popup', NewsletterPopup);

/* --- <localization-form>: country / currency selector --------------------- */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-loc-toggle]');
    this.list = this.querySelector('[data-loc-list]');
    this.toggle?.addEventListener('click', () => {
      const willOpen = this.list.hasAttribute('hidden');
      this.list.toggleAttribute('hidden', !willOpen);
      this.toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
    document.addEventListener('click', (e) => {
      if (this.contains(e.target)) return;
      this.list?.setAttribute('hidden', '');
      this.toggle?.setAttribute('aria-expanded', 'false');
    });
  }
}
customElements.define('localization-form', LocalizationForm);

/* --- Wishlist (localStorage, no account needed) --------------------------- */
Atlas.wishlist = {
  KEY: 'atlas-wishlist',
  get() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch (e) {
      return [];
    }
  },
  save(list) {
    try {
      localStorage.setItem(this.KEY, JSON.stringify(list));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('atlas:wishlist-change'));
  },
  has(handle) {
    return this.get().indexOf(handle) !== -1;
  },
  toggle(handle) {
    const list = this.get();
    const i = list.indexOf(handle);
    if (i === -1) list.push(handle);
    else list.splice(i, 1);
    this.save(list);
  },
  remove(handle) {
    this.save(this.get().filter((h) => h !== handle));
  },
};

function initWishlist() {
  const sync = () => {
    const list = Atlas.wishlist.get();
    document.querySelectorAll('[data-wish]').forEach((b) => {
      const on = list.indexOf(b.dataset.handle) !== -1;
      b.classList.toggle('is-wished', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-wish-count]').forEach((el) => {
      el.textContent = list.length;
    });
    document.documentElement.classList.toggle('has-wishlist', list.length > 0);
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wish]');
    if (!btn) return;
    e.preventDefault();
    Atlas.wishlist.toggle(btn.dataset.handle);
  });
  document.addEventListener('atlas:wishlist-change', sync);
  sync();
}

/* --- <wishlist-grid> ------------------------------------------------------ */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wish-grid]');
    this.empty = this.querySelector('[data-wish-empty]');
    this.render();
    document.addEventListener('atlas:wishlist-change', () => this.render());
  }
  escHtml(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }
  async render() {
    const handles = Atlas.wishlist.get();
    if (!handles.length) {
      this.empty?.removeAttribute('hidden');
      if (this.grid) this.grid.innerHTML = '';
      return;
    }
    this.empty?.setAttribute('hidden', '');
    const root = window.Shopify?.routes?.root || '/';
    const products = await Promise.all(
      handles.map((h) =>
        fetch(`${root}products/${h}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    if (this.grid) this.grid.innerHTML = products.filter(Boolean).map((p) => this.card(p)).join('');
  }
  card(p) {
    const img = p.featured_image || (p.images && p.images[0]) || '';
    const heart =
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 20.5 4.2 12.9a4.6 4.6 0 0 1 0-6.6 4.6 4.6 0 0 1 6.5 0l1.3 1.3 1.3-1.3a4.6 4.6 0 0 1 6.5 0 4.6 4.6 0 0 1 0 6.6z"/></svg>';
    return `
      <article class="wish-card">
        <button class="wish-card__remove is-wished" type="button" data-wish data-handle="${p.handle}" aria-label="Remove">${heart}</button>
        <a class="wish-card__media" href="${p.url}">${img ? `<img src="${img}" alt="${this.escHtml(p.title)}" loading="lazy">` : ''}</a>
        <a class="wish-card__info" href="${p.url}">
          <h3 class="wish-card__title h-mono">${this.escHtml(p.title)}</h3>
          <span class="wish-card__price">${Atlas.money(p.price)}</span>
        </a>
      </article>`;
  }
}
customElements.define('wishlist-grid', WishlistGrid);

function boot() {
  initReveal();
  initCart();
  initHeroScroll();
  initSectionFade();
  initWishlist();
  initA11y();
}
if (document.readyState !== 'loading') {
  boot();
} else {
  document.addEventListener('DOMContentLoaded', boot);
}

/* In the theme editor a section is re-rendered when its settings change. The
   scroll-reveal observer only watches elements present at load, so reveal the
   freshly inserted content immediately and re-sync wishlist state. */
document.addEventListener('shopify:section:load', (e) => {
  const root = e.target;
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  if (root.classList && root.classList.contains('section-fx')) root.classList.add('section-fx--in');
  root.querySelectorAll('.section-fx').forEach((el) => el.classList.add('section-fx--in'));
  document.dispatchEvent(new CustomEvent('atlas:wishlist-change'));
});
